import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type {
  GetPublishedExperienceInput,
  ListExperienceEventInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceEventsQueryKey } from '@/utils/queryKeys';
import { ExperienceEventFragment } from '../../events/fragments/ExperienceEventFragment';
import { fetchAllExperienceEvents } from '../lib/eventPages.lib';
import type { GetPublishedPageExperienceEventsQuery } from './useGetPublishedPageExperienceEvents.generated';

type RawEvent =
  GetPublishedPageExperienceEventsQuery['getPublishedExperience']['events'][number];

const query = gql`
  ${ExperienceEventFragment}
  query GetPublishedPageExperienceEvents(
    $input: GetPublishedExperienceInput!
    $filters: ListExperienceEventInput!
  ) {
    getPublishedExperience(input: $input) {
      events(filters: $filters) {
        ...ExperienceEvent
      }
    }
  }
`;

const selector = (data: GetPublishedPageExperienceEventsQuery): RawEvent[] => {
  return data.getPublishedExperience.events;
};

export const useGetPublishedPageExperienceEvents = (
  experienceSlug: string | undefined,
  filters: ListExperienceEventInput
) => {
  const queryKey = createExperienceEventsQueryKey(experienceSlug, filters);

  return useQuery<
    GetPublishedPageExperienceEventsQuery,
    QueryError,
    RawEvent[]
  >({
    enabled: Boolean(experienceSlug),
    queryKey,
    select: selector,
    queryFn: async () => {
      const input: GetPublishedExperienceInput = {
        experienceSlug: experienceSlug!, // Covered by enabled above
      };
      const events = await fetchAllExperienceEvents(
        filters,
        async (pageFilters) => {
          const data =
            await gqlClient.request<GetPublishedPageExperienceEventsQuery>(
              query,
              { input, filters: pageFilters }
            );
          return selector(data);
        }
      );
      // Re-wrapped in the document's own shape so the cached value — and what
      // @normy normalizes out of it — is what an untruncated single response
      // would have been.
      return { getPublishedExperience: { events } };
    },
  });
};
